/**
 * API functions for system settings and notification defaults.
 * Feature: System Administration Interface (001-admin-settings)
 * 
 * Uses fetcher utility with automatic JWT token inclusion.
 * Read endpoints are public, update endpoints require Admin privileges.
 */

import { fetcher } from '../utils/fetcher';
import type {
  SystemSettings,
  SystemSettingsUpdate,
  NotificationDefaults,
  NotificationDefaultsUpdate
} from '../types/system-settings';

const API_URL = '/api/v1/system-settings';

/**
 * Get system settings (public read access).
 * Returns application-wide configuration settings.
 */
export const getSystemSettings = async (): Promise<SystemSettings> => {
  return await fetcher(`${API_URL}/read`);
};

/**
 * Update system settings (admin only).
 * Modifies application-wide configuration.
 */
export const updateSystemSettings = async (
  updates: SystemSettingsUpdate
): Promise<SystemSettings> => {
  return await fetcher(`${API_URL}/update`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates)
  });
};

/**
 * Get notification defaults (public read access).
 * Returns default notification preferences for new users.
 */
export const getNotificationDefaults = async (): Promise<NotificationDefaults> => {
  return await fetcher(`${API_URL}/notification-defaults/read`);
};

/**
 * Update notification defaults (admin only).
 * Modifies default notification preferences for new users.
 */
export const updateNotificationDefaults = async (
  updates: NotificationDefaultsUpdate
): Promise<NotificationDefaults> => {
  return await fetcher(`${API_URL}/notification-defaults/update`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates)
  });
};
